<template>
  <div class="pre-bids-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :checkboxes="true" :search="tableData.search"
                   :enable-simple-search="false" enable-frontend-export
                   export-permission-name="export_auction" requires-min-height
                   @paramsChanged="getTableData" selectable filter-toggle-string="pre-bids-table"
                   show-toggle-columns :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns" export-vuex-module-name="preBids">

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary">
          <b-dropdown-item v-permission="'change_prebid'" @click="onNewBidClick">
            {{ $t('New Bids') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'" v-if="!isTimed"
                           @click="onBatchTelephoneBidGenerationClick()">
            {{ $t('Generate Batch Tel. Bids') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'export_auction'" @click="onGenerateTelephoneBidsPdfClick"
                           attribute="auction_uuid" :disabled="tableData.rows.length === 0">
            {{ $t('Print Telephone Bids') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'export_auction'" @click="onBatchConfirmClick(selectedItems)"
                           :disabled="selectedItems.size === 0" v-if="!hasArtisioTimedProvider">
            {{ $t('Confirm Bids') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'export_auction'" @click="onBatchUnconfirmClick(selectedItems)"
                           :disabled="selectedItems.size === 0" v-if="!hasArtisioTimedProvider">
            {{ $t('Reject Bids') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'delete_prebid'" :disabled="selectedItems.size === 0"
                           @click="onBatchRemoveClick(selectedItems)" v-if="!hasArtisioTimedProvider">
            {{ $t('Delete') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'" @click="onManageClerksClick()" v-if="!isTimed">
            {{ $t('Manage Clerks') }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="!tableData.rows.length > 0" v-permission="'change_auction'"
                           @click="onAssignClerksClick()" v-if="!isTimed">
            {{ $t('Assign Clerks') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="pre-bids-table">
          <pre-bid-search :update-search-model="updateSearchModel"
                          :get-table-data="getTableData"
                          :vuex-search-model="tableData.searchModel"/>
        </search-collapse>
      </template>
      <template slot="cell(created_at)" slot-scope="{item: bid}">
        {{ bid.created_at | toDateTime }}
      </template>
      <template slot="cell(updated_at)" slot-scope="{item: bid}">
        {{ bid.updated_at | toDateTime }}
      </template>
      <template slot="cell(date)" slot-scope="{item: bid}">
        {{ bid.date | toDateTime }}
      </template>
      <template slot="cell(bidder)" slot-scope="{item: bid}">
        <template v-if="bid.bidder">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: bid.bidder.customer.uuid }}">
            {{ bid.bidder.customer.display_name }}
          </router-link>
          <p v-else> {{ bid.bidder.customer.display_name }} </p>
        </template>
      </template>
      <template slot="cell(type)" slot-scope="{item: bid}">
        <span v-as-status="bid.type">{{ bid.type | removeUnderscores | toUpperCaseEveryWord }}</span>
      </template>
      <template slot="cell(amount)" slot-scope="{item: bid}">
        {{ bid.amount | asCurrency(sale.currency.symbol) }}
      </template>
      <template slot="cell(lot_no)" slot-scope="{item: bid}">
        {{ bid.lot_no }}
        <action-buttons :view="false" :remove="false" @editClick="onEditClick(bid)"/>
      </template>
      <template slot="cell(status)" slot-scope="{item: bid}">
        <span v-as-status="bid.status">{{ bid.status | toUpperCaseFirst }}</span>
      </template>
      <template slot="cell(clerk)" slot-scope="{item: bid}">
        <span v-if="bid.clerk_uuid && bid.clerk">
          {{ bid.clerk.name }}
        </span>
      </template>
      <template slot="cell(is_winner)" slot-scope="{item: bid}">
        <span class="badge bg-success text-white" v-if="bid.is_winner">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>
      <template slot="cell(lot_title)" slot-scope="{item: bid}">
        <div v-if="bid.lot && bid.lot.inventory">
          {{ bid.lot.inventory.dynamic_fields | attributeByMasterLanguage('title') | striptags }}
        </div>
      </template>
      <template slot="cell(lot_description)" slot-scope="{item: bid}">
        <div v-if="bid.lot && bid.lot.inventory">
          {{ bid.lot.inventory.dynamic_fields | attributeByMasterLanguage('description') | striptags }}
        </div>
      </template>
      <template slot="cell(actions)" slot-scope="{item: bid, toggleDetails}">

        <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v" class="icon"/>
          </template>
          <b-dd-item @click="markBidAsWinnerFromPreBid(bid)" v-permission="'auction_results_auction'">
            <font-awesome-icon icon="check" style="font-size: .9em" class="mr-1"/>
            {{ $t('Mark As Winner') }}
          </b-dd-item>
          <b-dd-item v-permission="'view_receipt'" @click="toggleDetails" v-if="bid.history.length > 1">
            <font-awesome-icon icon="history" style="font-size: .9em" class="mr-1"/>
            {{ $t('History') }}
          </b-dd-item>
          <b-dd-item v-permission="'view_receipt'" @click="onBatchRemoveClick(new Set([bid]))"
                     variant="danger">
            <font-awesome-icon icon="trash" style="font-size: .9em" class="mr-1"/>
            {{ $t('Delete') }}
          </b-dd-item>
        </b-dropdown>

      </template>

      <template v-slot:row-details="{item}">
        <div class="container">
          <div class="row">
            <div class="col-md-6">
              <table class="table table-sm">
                <tr>
                  <th>{{ $t('Amount') }}</th>
                  <th>{{ $t('Type') }}</th>
                  <th>{{ $t('Creation Date') }}</th>
                  <th>{{ $t('Date') }}</th>
                  <th>{{ $t('Phone 1') }}</th>
                  <th>{{ $t('Phone 2') }}</th>
                  <th>{{ $t('Status') }}</th>
                  <th class="text-center">{{ $t('Actions') }}</th>
                </tr>
                <tr v-for="bid in item.history" :key="`bid-history-row-${bid.uuid}`">
                  <td>{{ bid.amount | asCurrency(sale.currency.symbol) }}</td>
                  <td>{{ bid.type | toUpperCaseFirst | removeUnderscores }}</td>
                  <td>{{ bid.created_at | toDateTime }}</td>
                  <td>{{ bid.date | toDate }}</td>
                  <td>{{ bid.phone }}</td>
                  <td>{{ bid.phone2 }}</td>
                  <td>
                    <span v-as-status="bid.status">{{ bid.status | toUpperCaseFirst }}</span>
                  </td>
                  <td class="text-center">
                    <b-tooltip :target="`bid-history-remove-btn-${bid.uuid}`" :title="$t('Delete')"/>
                    <b-button :id="`bid-history-remove-btn-${bid.uuid}`" v-permission="'change_auction'"
                              variant="danger" size="sm" @click="onRemoveHistoryBidClick(item.uuid, bid.uuid)">
                      <font-awesome-icon icon="trash"></font-awesome-icon>
                    </b-button>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </template>

    </artisio-table>
    <pre-bids-modal/>
    <manage-clerks-modal/>
    <assign-clerks-modal/>
    <generate-pdf-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import {PRE_BIDS_TABLE_NAME, SOURCE_PRE_BIDS_TABLE} from "../constants";
  import PreBidsModal from "./modals/PreBidsModal.vue";
  import i18n from "../../shared/i18n";
  import PreBidSearch from "./PreBidSearch";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import PreBidsService from "./PreBidsService";
  import _ from 'lodash';
  import ManageClerksModal from "@/modules/pre-bids/modals/ManageClerksModal.vue";
  import AssignClerksModal from "@/modules/pre-bids/modals/AssignClerksModal.vue";
  import GeneratePdfModal from "@/modules/pre-bids/modals/GeneratePdfModal.vue";
  import {ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";
  import cloneDeep from "lodash/cloneDeep";

  const {mapActions, mapState} = createNamespacedHelpers('preBids');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "PreBidsTable",
    components: {
      SearchCollapse,
      PreBidSearch,
      PreBidsModal,
      ActionButtons,
      ArtisioTable,
      ManageClerksModal,
      AssignClerksModal,
      GeneratePdfModal,
    },
    computed: {
      ...mapState({
        tableSource: state => state.tableSource,
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        sale: state => state.tableData[state.tableSource].sale,
      }),
      ...globalMapState({
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
      }),
      isTimed() {
        return this.sale && this.sale.type === 'timed'
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: [],
          tableName: `${PRE_BIDS_TABLE_NAME}.${this.tableSource}`,
        }
      },
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f => f.isVisible);
      },
    },
    methods: {
      ...mapActions([
        'getTableData',
        'showModal',
        'batchRemovePreBids',
        'getTableParams',
        'updateSearchModel',
        'showPreBidEditModal',
        'removeHistoryBid',
        'createWinnerBidFromPreBid',
        'showManageClerksModal',
        'showAssignClerksModal',
        'getAssignClerksPreBidsData',
        'showGeneratePdfModal',
        'updateToggleColumns',
        'showBatchTelephoneBidGenerationModal',
      ]),
      onNewBidClick() {
        this.showModal({
          source: SOURCE_PRE_BIDS_TABLE,
          object: null,
          sale: _.cloneDeep(this.sale)
        });
      },
      onEditClick(item) {
        this.showPreBidEditModal({object: item, source: SOURCE_PRE_BIDS_TABLE, sale: _.cloneDeep(this.sale)})
      },
      async onBatchConfirmClick(items) {
        this.processBatchConfirmation(items, 'confirmed')
      },
      onBatchUnconfirmClick(items) {
        this.processBatchConfirmation(items, 'rejected')
      },
      async processBatchConfirmation(items, status) {
        let requestData = [];

        for (let item of items) {
          if (item.status === 'confirmed') {
            this.$errorDialog(this.$i18n.t("You can not confirm bid(s) that have already been confirmed. Please, remove confirmed bid(s) from selection and try again."))
            return
          }
          requestData.push({uuid: item.uuid, status});
        }

        let question = this.$i18n.t('Are you sure you want to mark following bid(s) as confirmed?');
        let successMsg = this.$i18n.t('Bid(s) were successfully marked confirmed');

        if (status === 'rejected') {
          question = this.$i18n.t('Are you sure you want to mark following bid(s) as rejected?');
          successMsg = this.$i18n.t('Bid(s) were successfully marked rejected');
        }

        let success = await this.$confirm(question, successMsg, PreBidsService.batchConfirmation, requestData);

        if (success) {
          this.getTableData();
          this.$refs.preBidsTable.clearSelected();
        }
      },
      async onBatchRemoveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));

        this.$confirm(
          this.$i18n.t("Are you sure you want to delete selected bids?"),
          this.$i18n.t('Bid(s) were successfully deleted'),
          this.batchRemovePreBids,
          uuids
        );
      },
      onGenerateTelephoneBidsPdfClick() {
        this.showGeneratePdfModal({
          source: SOURCE_PRE_BIDS_TABLE,
          object: _.cloneDeep(this.sale)
        });
      },
      async onGeneratePdfClick() {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Commission Bids')});
        let response = await PreBidsService.getPreBidsPdfData({
          auction_uuid: _.cloneDeep(this.sale).uuid
        });
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
            filename: `Pre bids list`
          })
        }
      },
      async onRemoveHistoryBidClick(bidUuid, historicBidUuid) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following historic bid?"),
          this.$i18n.t('Historic bid was successfully deleted'),
          this.removeHistoryBid,
          {bidUuid, historicBidUuid},
        );
      },
      async markBidAsWinnerFromPreBid(bid) {
        let success = await this.$confirm(this.$i18n.t("Are you sure you want to mark following commission bid as winning bid?"))
        if (!success) {
          return
        }

        const reservation_proposals = (await LotsService.getLatestReservationProposals({pre_bid_uuids: [bid.uuid]})).body
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) return
        }

        let initialResponse = await this.createWinnerBidFromPreBid({uuid: bid.uuid});
        if (initialResponse.success) {
          this.$toast('success', this.$i18n.t('Bid was successfully created'));
          return;
        }

        if (initialResponse.body.confirmation) {
          let success = await this.$confirm(initialResponse.body.confirmation[0]);
          if (success) {
            let secondResponse = await this.createWinnerBidFromPreBid({uuid: bid.uuid, data: {force_send: true}});
            if (secondResponse.success) {
              this.$toast('success', this.$i18n.t("Bid was successfully created"));
            } else {
              if (secondResponse.toastError) {
                this.$errorDialog(secondResponse.toastError);
              } else {
                if (secondResponse && secondResponse.body.non_field_errors) {
                  this.$errorDialog(secondResponse.body.non_field_errors[0]);
                }
              }
            }
          }
        } else if (initialResponse.body.non_field_errors) {
          this.$errorDialog(initialResponse.body.non_field_errors[0]);
        }
      },
      onManageClerksClick() {
        this.showManageClerksModal({
          source: SOURCE_PRE_BIDS_TABLE,
          object: _.cloneDeep(this.sale)
        });
      },
      onAssignClerksClick() {
        this.getAssignClerksPreBidsData({auction_uuid: this.sale.uuid})

        this.showAssignClerksModal({
          source: SOURCE_PRE_BIDS_TABLE,
          object: _.cloneDeep(this.sale)
        });
      },
      onBatchTelephoneBidGenerationClick() {
        this.showBatchTelephoneBidGenerationModal({
          source: SOURCE_PRE_BIDS_TABLE,
          sale: _.cloneDeep(this.sale)
        });
      }
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
      .lot-no-col {
        min-width: 60px;
      }

      .paddle-number-col {
        min-width: 80px;
      }

      .bidder-col {
        min-width: 130px;
      }

      .amount-col {
        min-width: 90px;
      }

      .type-col {
        min-width: 110px;
      }

      .status-col {
        min-width: 90px;
      }

      .created-at-col {
        min-width: 140px;
      }

      .is-winner-col {
        min-width: 70px;
      }

      .actions-col {
        min-width: 70px;
      }

      .clerk-col {
        min-width: 110px;
      }

      .lot_title-col {
        min-width: 320px;
        max-width: 500px;
        text-wrap: wrap;
      }

      .lot_description-col {
        min-width: 320px;
        max-width: 500px;
        text-wrap: wrap;
      }
    }
  }

</style>

